import {
  billingScreen,
  fleetScreen,
  greenzoneScreen,
  packagesScreen,
  renderTui,
  storefrontScreen,
  subscribersScreen,
  usageScreen,
  walletScreen,
  type Screen,
} from "@carrier/screens";

/**
 * `carrier dash` — the terminal half of the screen kit.
 *
 * Each domain reuses the same builder the MCP panel uses, so the terminal and
 * the rendered panel cannot drift: if a number is wrong in one it is wrong in
 * both, which is a far better failure than the two disagreeing silently. That
 * disagreement is exactly what went unnoticed in `fleet_health_app`.
 *
 * Data comes from the MCP tools through the existing capability layer, so this
 * adds a rendering surface, not a second API client.
 */

export const DASH_DOMAINS = [
  "fleet",
  "subscribers",
  "usage",
  "packages",
  "billing",
  "wallet",
  "greenzone",
  "storefront",
] as const;

export type DashDomain = (typeof DASH_DOMAINS)[number];

export function isDashDomain(value: string): value is DashDomain {
  return (DASH_DOMAINS as readonly string[]).includes(value);
}

/**
 * Tool that backs each domain.
 *
 * `fleet` deliberately calls the `_app` variant: it returns the built `Screen`
 * in `structuredContent`, so the terminal renders the very object the panel
 * renders. The prose `fleet_health` returns markdown, which a screen cannot be
 * reconstructed from without inventing a parser — the sort of second
 * implementation that let the panel and the report disagree in the first place.
 */
export const DASH_TOOL: Record<DashDomain, string> = {
  fleet: "fleet_health_app",
  subscribers: "list_subscribers",
  usage: "subscriber_usage",
  packages: "list_package_templates",
  billing: "billing_events",
  wallet: "wallet_balance",
  greenzone: "greenzone_whitelist_list",
  storefront: "",
};

/** A `Screen` the server already built, if the tool returned one. */
export function screenFromStructured(structured: unknown): Screen | undefined {
  const rec = structured as { screen?: unknown } | undefined;
  const screen = rec?.screen as Screen | undefined;
  if (screen && typeof screen === "object" && Array.isArray(screen.sections) && screen.title) {
    return screen;
  }
  return undefined;
}

const num = (value: unknown, fallback = 0): number => {
  const n = Number(value);
  return Number.isFinite(n) ? n : fallback;
};

const str = (value: unknown, fallback = ""): string =>
  value === undefined || value === null ? fallback : String(value);

/** Pull the first array found under any of `keys`, or a bare array. */
function rows(payload: unknown, keys: string[]): Record<string, unknown>[] {
  if (Array.isArray(payload)) return payload as Record<string, unknown>[];
  const rec = (payload ?? {}) as Record<string, unknown>;
  for (const key of keys) {
    const candidate = rec[key];
    if (Array.isArray(candidate)) return candidate as Record<string, unknown>[];
  }
  return [];
}

/**
 * Build the screen for a domain from that domain's tool payload.
 *
 * Unknown or unexpected payloads produce an empty screen rather than a guess —
 * the renderer then says plainly that nothing came back, which is the honest
 * result and the one that makes a broken data path visible.
 */
export function buildDashScreen(domain: DashDomain, payload: unknown): Screen {
  switch (domain) {
    case "fleet": {
      const accounts = rows(payload, ["accounts", "account"]);
      return fleetScreen({
        active: num((payload as Record<string, unknown>)?.active),
        suspended: num((payload as Record<string, unknown>)?.suspended),
        inventory: num((payload as Record<string, unknown>)?.inventory),
        other: num((payload as Record<string, unknown>)?.other),
        accounts: accounts.map((a) => ({
          name: str(a.name ?? a.accountId, "?"),
          balance: num(a.balance),
          active: num(a.active),
          suspended: num(a.suspended),
          inventory: num(a.inventory),
          other: num(a.other),
          packageOnly: Boolean(a.packageOnly),
        })),
      });
    }
    case "subscribers":
      return subscribersScreen(
        rows(payload, ["subscribers", "subscriber", "data"]).map((s) => ({
          iccid: str(s.iccid ?? s.ICCID, "?"),
          msisdn: s.msisdn ? str(s.msisdn) : undefined,
          status: str(s.status ?? s.statusStr, "unknown"),
          account: s.accountName ? str(s.accountName) : undefined,
          dataUsedBytes: s.dataUsed === undefined ? undefined : num(s.dataUsed),
        })),
      );
    case "usage":
      return usageScreen({
        subject: str((payload as Record<string, unknown>)?.subject, "Subscriber"),
        timeline: rows(payload, ["timeline", "days", "usage"]).map((p) => ({
          date: str(p.date ?? p.day, "?"),
          bytes: num(p.bytes ?? p.dataUsed),
        })),
        countries: rows(payload, ["countries"]).map((c) => ({
          country: str(c.country ?? c.countryName, "?"),
          bytes: num(c.bytes ?? c.dataUsed),
        })),
      });
    case "packages":
      return packagesScreen(
        rows(payload, ["template", "templates", "data"]).map((t) => ({
          name: str(t.prepaidpackagetemplatename ?? t.name, "?"),
          id: str(t.prepaidpackagetemplateid ?? t.id, "?"),
          dataLimitBytes: t.databyte === undefined ? undefined : num(t.databyte),
          validityDays: t.perioddays === undefined ? undefined : num(t.perioddays),
          price: t.cost === undefined ? undefined : num(t.cost),
          recurring: Boolean(t.recurring),
        })),
      );
    case "billing":
      return billingScreen({
        balance: (payload as Record<string, unknown>)?.balance === undefined
          ? undefined
          : num((payload as Record<string, unknown>).balance),
        currency: str((payload as Record<string, unknown>)?.currency, ""),
        events: rows(payload, ["events", "data"]).map((e) => ({
          date: str(e.date ?? e.timestamp, "?"),
          description: str(e.description ?? e.type, "—"),
          amount: num(e.amount),
        })),
      });
    case "wallet": {
      const p = (payload ?? {}) as Record<string, unknown>;
      // `wallet_balance` reports EUR *cents* under snake_case keys, while
      // walletScreen renders currency units via money(). Reading `p.balance`
      // and `p.threshold` — keys the tool never returns — made the dashboard
      // show a 0.00 balance and a "—" threshold for every wallet, funded or
      // not. The two auto-top-up amounts are also null until auto-top-up is
      // configured, which has to read as unset rather than as zero.
      const eur = (value: unknown): number | undefined =>
        value === undefined || value === null ? undefined : num(value) / 100;
      return walletScreen({
        balance: eur(p.balance_eur_cents ?? p.balance) ?? 0,
        currency: str(p.currency, "€"),
        autoTopupEnabled: Boolean(p.autoTopupEnabled ?? p.auto_topup_enabled),
        threshold: eur(p.auto_topup_threshold_cents ?? p.threshold),
        credits: p.credits === undefined || p.credits === null ? undefined : num(p.credits),
      });
    }
    case "greenzone":
      return greenzoneScreen(
        rows(payload, ["entries", "whitelist", "data"]).map((e) => ({
          value: str(e.value ?? e.prefix ?? e.destination, "?"),
          note: e.note ? str(e.note) : undefined,
        })),
      );
    case "storefront": {
      const p = (payload ?? {}) as Record<string, unknown>;
      return storefrontScreen({
        brand: str(p.brand ?? p.project, "storefront"),
        url: p.url ? str(p.url) : undefined,
        target: p.target ? str(p.target) : undefined,
        verified: p.verified === undefined ? null : Boolean(p.verified),
        diagnosis: p.diagnosis ? str(p.diagnosis) : undefined,
        secretsStaged: Array.isArray(p.secrets_staged) ? (p.secrets_staged as string[]) : undefined,
        plans: p.plans === undefined ? undefined : num(p.plans),
      });
    }
  }
}

/** Render a domain screen for the terminal. */
export function renderDash(
  domain: DashDomain,
  payload: unknown,
  opts: { width?: number; color?: boolean } = {},
): string {
  return renderTui(buildDashScreen(domain, payload), opts);
}
